import React, { useState, useEffect } from 'react';
import {
  Search, Eye, Calendar, User, Fuel, Download, X,
  ChevronLeft, ChevronRight, Loader2, FileText, Printer,
  CheckCircle, Clock, XCircle, AlertCircle
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import megalogo from '../Image/megalogo.png';

export default function FuelRequisitionApproval() {
  const [fuelRequests, setFuelRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [approving, setApproving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setCurrentUser({
        name: `${userData.firstName} ${userData.lastName}`.trim(),
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role || "User"
      });
    }
    fetchFuelRequests();
    fetchWebsiteInfo();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [searchTerm, fuelRequests]);

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed to fetch website info');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) setWebsiteInfo(activeWebsite);
    } catch (error) {
      console.error('Error fetching website info:', error);
    }
  };

  const fetchFuelRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition`);
      if (!response.ok) { setFuelRequests([]); setFilteredRequests([]); return; }
      const data = await response.json();
      const requestArray = Array.isArray(data) ? data : (data ? [data] : []);
      setFuelRequests(requestArray);
      setFilteredRequests(requestArray);
    } catch (error) {
      console.error('❌ Error fetching fuel requests:', error);
      setFuelRequests([]); setFilteredRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...fuelRequests];
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requestorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

const handleApprove = async () => {
  if (!selectedRequest) return;
  if (!currentUser) {
    Swal.fire({ title: 'Error!', text: 'You must be logged in to approve requests.', icon: 'error', confirmButtonColor: '#ef4444' });
    return;
  }

  const result = await Swal.fire({
    title: 'Approve Request?',
    html: `
      <div style="text-align: left;">
        <p><strong>Control No:</strong> ${selectedRequest.controlNo}</p>
        <p><strong>Requestor:</strong> ${selectedRequest.requestorName}</p>
        <p><strong>Quantity:</strong> ${selectedRequest.quantityLiters} Liters</p>
        <hr style="margin: 10px 0;">
        <p><strong>Approver:</strong> ${currentUser.name}</p>
      </div>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#22c55e',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Yes, Approve',
    cancelButtonText: 'Cancel'
  });

  if (result.isConfirmed) {
    setApproving(true);
    try {
      const updatedRequest = {
        ...selectedRequest,
        approvedBy: currentUser.name,
        approvedBySignature: null,
      };

      const response = await fetch(`${API_BASE_URL}api/FuelRequisition/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedRequest)
      });

      if (response.ok) {
        // ✅ Log approval success
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "APPROVE",
            module: "Fuel Requisition",
            description: `Approved Fuel Requisition #${selectedRequest.controlNo} (ID: ${selectedRequest.id}) requested by ${selectedRequest.requestorName} - Plate: ${selectedRequest.plateNumber}, Qty: ${selectedRequest.quantityLiters}L`,
            userId: user?.id || user?.userId,
            username: user?.username,
            fullName: currentUser.name || user?.username,
          }),
        }).catch(err => console.error("Log failed:", err));

        await Swal.fire({ title: 'Approved!', text: 'Fuel requisition has been approved successfully.', icon: 'success', confirmButtonColor: '#3b82f6' });
        await fetchFuelRequests();
        setShowModal(false);
      } else {
        throw new Error('Failed to approve');
      }
    } catch (error) {
      // ✅ Log approval failed
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "APPROVE-FAILED",
          module: "Fuel Requisition",
          description: `Failed to approve Fuel Requisition #${selectedRequest.controlNo} (ID: ${selectedRequest.id}). Error: ${error.message}`,
          userId: user?.id || user?.userId,
          username: user?.username,
          fullName: currentUser.name || user?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      Swal.fire({ title: 'Error!', text: 'Failed to approve the request. Please try again.', icon: 'error', confirmButtonColor: '#ef4444' });
    } finally {
      setApproving(false);
    }
  }
};

const handleReject = async () => {
  if (!selectedRequest) return;
  if (!currentUser) {
    Swal.fire({ title: 'Error!', text: 'You must be logged in to reject requests.', icon: 'error', confirmButtonColor: '#ef4444' });
    return;
  }

  const result = await Swal.fire({
    title: 'Reject Request?',
    html: `
      <div style="text-align: left;">
        <p><strong>Control No:</strong> ${selectedRequest.controlNo}</p>
        <p><strong>Requestor:</strong> ${selectedRequest.requestorName}</p>
        <hr style="margin: 10px 0;">
        <p><strong>Rejected By:</strong> ${currentUser.name}</p>
      </div>
    `,
    input: 'textarea',
    inputLabel: 'Rejection Reason',
    inputPlaceholder: 'Enter reason for rejection...',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Yes, Reject',
    cancelButtonText: 'Cancel',
    inputValidator: (value) => { if (!value) return 'Please provide a reason for rejection'; }
  });

  if (result.isConfirmed) {
    setApproving(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...selectedRequest,
          rejectedBy: currentUser.name,
          rejectionDate: new Date().toISOString(),
          rejectionReason: result.value,
          status: 'Rejected'
        })
      });

      if (response.ok) {
        // ✅ Log rejection success
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "REJECT",
            module: "Fuel Requisition",
            description: `Rejected Fuel Requisition #${selectedRequest.controlNo} (ID: ${selectedRequest.id}) requested by ${selectedRequest.requestorName}. Reason: ${result.value}`,
            userId: user?.id || user?.userId,
            username: user?.username,
            fullName: currentUser.name || user?.username,
          }),
        }).catch(err => console.error("Log failed:", err));

        await Swal.fire({ title: 'Rejected!', text: 'Fuel requisition has been rejected.', icon: 'success', confirmButtonColor: '#3b82f6' });
        await fetchFuelRequests();
        setShowModal(false);
      } else {
        throw new Error('Failed to reject');
      }
    } catch (error) {
      // ✅ Log rejection failed
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "REJECT-FAILED",
          module: "Fuel Requisition",
          description: `Failed to reject Fuel Requisition #${selectedRequest.controlNo} (ID: ${selectedRequest.id}). Error: ${error.message}`,
          userId: user?.id || user?.userId,
          username: user?.username,
          fullName: currentUser.name || user?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      Swal.fire({ title: 'Error!', text: 'Failed to reject the request.', icon: 'error', confirmButtonColor: '#ef4444' });
    } finally {
      setApproving(false);
    }
  }
};

  const isApproved = (request) =>
    request.approvedBy && request.approvedBy !== 'NULL' && request.approvedBy.trim() !== '';

  const getStatusBadge = (request) => {
    if (isApproved(request)) {
      return (
        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-green-200">
          <CheckCircle className="w-3.5 h-3.5" /> Approved
        </span>
      );
    } else if (request.status === 'Rejected') {
      return (
        <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200">
          <XCircle className="w-3.5 h-3.5" /> Rejected
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1.5 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-yellow-200">
          <Clock className="w-3.5 h-3.5" /> Pending
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // ── CLEAN PDF — minimal colors ──
  const exportToPDF = (request) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    let y = 14;

    // Logo
    try { doc.addImage(megalogo, 'PNG', margin, y, 25, 13); } catch (err) {}

    // Company info
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(20, 20, 20);
    doc.text(websiteInfo?.websiteName || 'MEGASOFT HYGIENIC PRODUCTS, INC.', pageWidth / 2, y + 5, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(websiteInfo?.websiteSlogan || 'A MEGASOFT IN EVERY HOME', pageWidth / 2, y + 10, { align: 'center' });
    if (websiteInfo?.websiteContact) {
      doc.text(websiteInfo.websiteContact, pageWidth / 2, y + 14, { align: 'center' });
    }

    // Doc code box
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.rect(pageWidth - margin - 42, y, 42, 16);
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('DOCUMENT CODE: SPR-F-009', pageWidth - margin - 40, y + 4.5);
    doc.text('ISSUE NO: 1  |  REVISION NO: 0', pageWidth - margin - 40, y + 8.5);
    doc.text(`DATE: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, y + 12.5);

    y += 20;

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageWidth - margin, y);

    y += 7;

    // Title
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('FUEL REQUISITION FORM', pageWidth / 2, y, { align: 'center' });

    // Blue underline accent only
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(0.8);
    doc.line(65, y + 2, 145, y + 2);

    // Status badge — small, right side, gray/dark
    const statusText = isApproved(request) ? 'APPROVED' : request.status === 'Rejected' ? 'REJECTED' : 'PENDING';
    const statusColor = isApproved(request) ? [34, 120, 60] : request.status === 'Rejected' ? [180, 40, 40] : [140, 100, 0];
    doc.setFillColor(...statusColor);
    doc.roundedRect(pageWidth - margin - 28, y - 5, 28, 7, 1, 1, 'F');
    doc.setFontSize(6.5);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text(statusText, pageWidth - margin - 14, y - 0.8, { align: 'center' });

    y += 9;

    // Control No
    doc.setDrawColor(180, 180, 180);
    doc.setFillColor(247, 247, 247);
    doc.setLineWidth(0.3);
    doc.rect(margin, y, pageWidth - 2 * margin, 9, 'FD');

    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('CONTROL #:', margin + 3, y + 6);

    doc.setFontSize(9);
    doc.setTextColor(30, 64, 175);
    doc.text(request.controlNo || '—', margin + 27, y + 6);

    y += 13;

    // ── REQUESTOR INFO ──
    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text('REQUESTOR INFORMATION', margin, y);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 5;

    const lCol = margin + 2;
    const rCol = pageWidth / 2 + 5;

    const drawField = (label, value, x, fy) => {
      doc.setFontSize(6.5); doc.setFont(undefined, 'normal'); doc.setTextColor(130, 130, 130);
      doc.text(label, x, fy);
      doc.setFontSize(8.5); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
      doc.text(value || '—', x, fy + 5);
    };

    drawField('Requestor Name', request.requestorName, lCol, y);
    drawField('Department', request.department || 'N/A', rCol, y);
    y += 11;
    drawField('Plate Number', request.plateNumber, lCol, y);
    drawField('Date & Time', `${formatDate(request.requestDate)}  |  ${formatTime(request.requestTime)}`, rCol, y);

    y += 14;

    // ── FUEL DETAILS ──
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('FUEL DETAILS', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 5;

    const colW = (pageWidth - 2 * margin) / 4;

    // Table header
    doc.setFillColor(240, 240, 240); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
    doc.rect(margin, y, pageWidth - 2 * margin, 7, 'FD');
    doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(70, 70, 70);
    doc.text('DESCRIPTION', margin + 3, y + 5);
    doc.text('LEVEL', margin + colW + 3, y + 5);
    doc.text('ACTUAL LITERS', margin + colW * 2 + 3, y + 5);
    doc.text('TRUCK ODOMETER', margin + colW * 3 + 3, y + 5);
    y += 7;

    // Table data
    doc.setFillColor(252, 252, 252); doc.setDrawColor(210, 210, 210);
    doc.rect(margin, y, pageWidth - 2 * margin, 8, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20);
    doc.text(String(request.description || '—'), margin + 3, y + 5.5);
    doc.text(String(request.level || '—'), margin + colW + 3, y + 5.5);
    doc.text(String(request.actualLiters ?? '—'), margin + colW * 2 + 3, y + 5.5);
    doc.text(String(request.truckOdometer ?? '—'), margin + colW * 3 + 3, y + 5.5);
    y += 10;

    // Qty + flow meter
    doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210);
    doc.rect(margin, y, pageWidth - 2 * margin, 14, 'FD');
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('Quantity of Liters Requested:', margin + 3, y + 5.5);
    doc.text('Flow Meter Beg:', margin + 3, y + 11);
    doc.text('Flow Meter End:', pageWidth / 2 + 3, y + 11);
    doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(8.5);
    doc.text(String(request.quantityLiters ?? '—'), margin + 57, y + 5.5);
    doc.text(String(request.flowMeterBeg ?? '—'), margin + 35, y + 11);
    doc.text(String(request.flowMeterEnd ?? '—'), pageWidth / 2 + 32, y + 11);
    y += 18;

    // ── PURPOSE ──
    if (request.purpose) {
      const purposeLines = doc.splitTextToSize(request.purpose, pageWidth - 2 * margin - 20);
      const purposeH = Math.max(12, purposeLines.length * 5 + 8);
      doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
      doc.rect(margin, y, pageWidth - 2 * margin, purposeH, 'FD');
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('PURPOSE:', margin + 3, y + 5.5);
      doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(7.5);
      doc.text(purposeLines, margin + 22, y + 5.5);
      y += purposeH + 6;
    }

    // ── SIGNATURES ──
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('APPROVALS & SIGNATURES', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 6;

    const sigW = (pageWidth - 2 * margin - 5) / 2;
    const sigH = 22;

    const drawSigBox = (x, boxY, label, name) => {
      doc.setFillColor(255, 255, 255); doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.25);
      doc.rect(x, boxY, sigW, sigH, 'FD');
      doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text(label, x + 3, boxY + 5);
      doc.setDrawColor(100, 100, 100); doc.setLineWidth(0.35);
      doc.line(x + 3, boxY + 16, x + sigW - 3, boxY + 16);
      if (name) {
        doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
        doc.text(name, x + 3, boxY + 14);
      }
      doc.setFontSize(5.5); doc.setFont(undefined, 'italic'); doc.setTextColor(140, 140, 140);
      doc.text('Signature & Date', x + 3, boxY + 20);
    };

    // Row 1
    drawSigBox(margin, y, 'REQUESTED BY', request.requestedBy || request.requestorName);
    drawSigBox(margin + sigW + 5, y, 'NOTED BY', request.notedBy);
    y += sigH + 4;

    // Row 2
    drawSigBox(margin, y, 'APPROVED BY', request.approvedBy);
    drawSigBox(margin + sigW + 5, y, 'GAS MAN / ASSIST BY', request.gasManAssistBy);
    y += sigH + 6;

    // Footer
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
    doc.setFontSize(6); doc.setFont(undefined, 'italic'); doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(`Control No: ${request.controlNo}  |  SPR-F-009`, pageWidth / 2, pageHeight - 4, { align: 'center' });

    doc.save(`Fuel_Requisition_${request.controlNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
              <Fuel className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fuel Requisition Approval</h1>
              <p className="text-gray-500 mt-1">Review and approve fuel requests</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Control No, Requestor, Plate #, Department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Loading fuel requests...</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-20">
              <Fuel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No fuel requests found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Requestor</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Plate #</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Qty (L)</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <FileText className="w-3.5 h-3.5" />
                            {request.controlNo}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{formatDate(request.requestDate)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{request.requestorName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono font-bold text-gray-900">{request.plateNumber}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-700">{request.department || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <Fuel className="w-3.5 h-3.5" />
                            {request.quantityLiters || 0} L
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getStatusBadge(request)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleViewRequest(request)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md text-sm font-semibold"
                            >
                              <Eye className="w-4 h-4" /> View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(endIndex, filteredRequests.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredRequests.length}</span> requests
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <span className="text-sm font-medium px-4">{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1">
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* MODAL */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-800 text-white px-8 py-6 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <FileText className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-bold">Fuel Requisition Approval</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-white/10 inline-block px-4 py-1.5 rounded-lg border border-white/20">
                      <p className="text-sm">Control No: <span className="font-bold text-base ml-2">{selectedRequest.controlNo}</span></p>
                    </div>
                    {getStatusBadge(selectedRequest)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => exportToPDF(selectedRequest)}
                    className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20" title="Download PDF">
                    <Download className="h-5 w-5" />
                  </button>
                  <button onClick={() => setShowModal(false)}
                    className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 bg-gray-50 flex-1 overflow-y-auto">

              {/* Requestor Info */}
              <div className="bg-white border-2 border-blue-100 rounded-xl p-6 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">Requestor Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Requestor Name</p>
                    <p className="text-base font-bold text-gray-900">{selectedRequest.requestorName}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Plate Number</p>
                    <p className="text-base font-bold text-gray-900 font-mono">{selectedRequest.plateNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Department</p>
                    <p className="text-base font-bold text-gray-900">{selectedRequest.department || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Date & Time</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <p className="text-base font-bold text-gray-900">
                        {formatDate(selectedRequest.requestDate)} <span className="text-blue-500">•</span> {formatTime(selectedRequest.requestTime)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fuel Details */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Fuel className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">Fuel Details</h3>
                </div>
                <div className="bg-blue-700 text-white rounded-xl p-4 mb-4 shadow-md">
                  <p className="text-sm font-semibold opacity-80">Quantity Requested</p>
                  <p className="text-3xl font-bold">{selectedRequest.quantityLiters || 0} <span className="text-xl font-normal">LITERS</span></p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Description', value: selectedRequest.description || 'N/A' },
                    { label: 'Level', value: selectedRequest.level || 'N/A' },
                    { label: 'Actual Liters', value: `${selectedRequest.actualLiters || 0} L` },
                    { label: 'Truck Odometer', value: selectedRequest.truckOdometer || 0 },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{item.label}</p>
                      <p className="text-sm font-bold text-gray-900">{item.value}</p>
                    </div>
                  ))}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 col-span-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Flow Meter</p>
                    <div className="flex items-center gap-4">
                      <div><span className="text-xs text-gray-500">Beg:</span> <span className="text-sm font-bold ml-1">{selectedRequest.flowMeterBeg || 0}</span></div>
                      <div className="h-5 w-px bg-gray-300"></div>
                      <div><span className="text-xs text-gray-500">End:</span> <span className="text-sm font-bold ml-1">{selectedRequest.flowMeterEnd || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purpose */}
              {selectedRequest.purpose && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-blue-700" />
                    <h3 className="text-sm font-bold text-blue-900 uppercase">Purpose</h3>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">{selectedRequest.purpose}</p>
                </div>
              )}

              {/* Approval Status */}
              <div className={`border-2 rounded-xl p-6 shadow-sm ${
                isApproved(selectedRequest) ? 'bg-green-50 border-green-200'
                : selectedRequest.status === 'Rejected' ? 'bg-red-50 border-red-200'
                : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center gap-2 mb-4">
                  {isApproved(selectedRequest) ? <CheckCircle className="w-6 h-6 text-green-600" />
                   : selectedRequest.status === 'Rejected' ? <XCircle className="w-6 h-6 text-red-600" />
                   : <AlertCircle className="w-6 h-6 text-yellow-600" />}
                  <h3 className="text-base font-bold text-gray-900">Approval Status</h3>
                </div>

                {isApproved(selectedRequest) ? (
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Approved By</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRequest.approvedBy}</p>
                    {selectedRequest.approvalDate && (
                      <p className="text-xs text-gray-500 mt-1">Approved on {formatDate(selectedRequest.approvalDate)}</p>
                    )}
                  </div>
                ) : selectedRequest.status === 'Rejected' ? (
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Rejected By</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRequest.rejectedBy}</p>
                    {selectedRequest.rejectionReason && (
                      <div className="mt-3 pt-3 border-t border-red-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Reason</p>
                        <p className="text-sm text-gray-800">{selectedRequest.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <p className="text-sm font-semibold text-gray-700">Awaiting approval decision</p>
                    </div>
                    <p className="text-sm text-gray-600">Review the details above and make your decision below.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t bg-gray-50 px-8 py-5 flex justify-between items-center gap-3 flex-shrink-0">
              <button onClick={() => exportToPDF(selectedRequest)}
                className="px-5 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2">
                <Printer className="w-4 h-4" /> Export PDF
              </button>

              <div className="flex gap-3">
                {!isApproved(selectedRequest) && selectedRequest.status !== 'Rejected' && (
                  <>
                    <button onClick={handleReject} disabled={approving}
                      className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                        : <><XCircle className="w-4 h-4" /> Reject</>}
                    </button>
                    <button onClick={handleApprove} disabled={approving}
                      className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                        : <><CheckCircle className="w-4 h-4" /> Approve</>}
                    </button>
                  </>
                )}
                <button onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-semibold transition-all shadow-md">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}